import { useState, useRef, useEffect } from "react"
import { Loader2, Layout, Search, X, Flame } from "lucide-react"
import { toast } from "sonner"
import { useTranslation, Trans } from "react-i18next"
import { useTemplates, usePopularTemplates, type Template } from "@/hooks/useTemplates"
import { useGeneratorStore } from "@/stores/generatorStore"
import { JustifiedRows } from "@/components/common/JustifiedRows"
import { localizeName, localizeDescription } from "@/lib/i18n-content"
import { TemplatePreviewModal } from "./TemplatePreviewModal"
import {
  extractSettingsFromTemplate,
  toTemplateThumbnailUrl,
} from "../lib/extract-settings-from-template"

/** 템플릿 카드 컴포넌트 */
function TemplateCard({
  template,
  categoryName,
  onUse,
  onPreview,
}: {
  template: Template
  categoryName: string
  onUse: () => void
  onPreview: () => void
}) {
  const { t } = useTranslation()
  const cssAspectRatio = template.aspectRatio?.replace(':', '/') ?? '1/1'

  return (
    <div
      className="group relative bg-white rounded-xl ring-1 ring-slate-200 overflow-hidden hover:ring-slate-300 hover:shadow-md transition-all cursor-pointer"
      onClick={onPreview}
    >
      {/* 썸네일 (이름은 썸네일 하단에 겹쳐 표시) */}
      <div
        className="bg-slate-100 relative overflow-hidden"
        style={{ aspectRatio: cssAspectRatio }}
      >
        {/* 카테고리 뱃지: 우측 상단 (좁은 세로형 카드에서는 한 줄로 말줄임) */}
        <div className="absolute top-2 left-2 right-2 z-10 flex justify-end">
          <span className="truncate px-1.5 py-0.5 bg-white/80 backdrop-blur-sm text-[11px] font-medium text-slate-500 rounded">
            {categoryName}
          </span>
        </div>

        {template.thumbnailUrl ? (
          <img
            src={toTemplateThumbnailUrl(template.thumbnailUrl, template.updatedAt) ?? ''}
            alt={localizeName(template)}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Layout className="w-12 h-12 text-slate-300" />
          </div>
        )}

        {/* 이름: 하단 전체 폭 반투명 바, 단어 단위로 줄바꿈해 최대 두 줄 */}
        <div className="absolute inset-x-0 bottom-0 z-10 bg-black/50 px-2.5 py-1.5">
          <p
            className="text-sm font-medium leading-snug text-center text-white text-shadow-xs line-clamp-2 break-keep wrap-anywhere"
            title={localizeName(template)}
          >
            {localizeName(template)}
          </p>
        </div>

        {/* 호버 시 Use 버튼 - 데스크톱 전용 (모바일은 프리뷰 모달에서 사용) */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none lg:pointer-events-auto">
          <button
            onClick={(e) => {
              e.stopPropagation()
              onUse()
            }}
            className="px-4 py-2 bg-white rounded-lg font-medium text-sm text-slate-800 hover:bg-slate-100 transition-colors shadow-lg"
          >
            {t('templates.use')}
          </button>
        </div>
      </div>
    </div>
  )
}


/** 스크롤 시 한 번에 더 렌더할 카드 수 */
const TEMPLATES_PAGE_SIZE = 24

interface TemplatesTabProps {
  selectedCategoryId: string | null
  onCategoryChange: (categoryId: string | null) => void
}

export function TemplatesTab({ selectedCategoryId, onCategoryChange }: TemplatesTabProps) {
  const { t } = useTranslation()
  const { data: categories, isLoading, error } = useTemplates()
  const isPopularSelected = selectedCategoryId === 'popular'
  const { data: popularTemplates, isLoading: isPopularLoading } = usePopularTemplates(isPopularSelected)
  const applySettings = useGeneratorStore((state) => state.applySettings)
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [visibleCount, setVisibleCount] = useState(TEMPLATES_PAGE_SIZE)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const observerTarget = useRef<HTMLDivElement>(null)

  const handleUseTemplate = (template: Template) => {
    const settings = extractSettingsFromTemplate(template)
    applySettings(settings)
    toast.success(t('templates.appliedToastTitle'), {
      description: t('templates.appliedToastDescription', { name: localizeName(template) }),
    })
  }

  // '기타'(uncategorized) 카테고리는 DB에 없으므로 id 기준으로 UI 카탈로그에서 번역
  const getCategoryDisplayName = (category: {
    id: string
    name: string
    nameI18n?: Record<string, string> | null
  }): string =>
    category.id === 'uncategorized' ? t('templates.uncategorized') : localizeName(category)

  // 모든 템플릿을 카테고리 정보와 함께 flatten
  const allTemplates = (categories ?? []).flatMap((category) =>
    category.templates.map((template) => ({
      ...template,
      categoryId: category.id,
      categoryName: getCategoryDisplayName(category),
    }))
  )

  // 인기 탭이면 popularTemplates 사용, 아니면 기존 카테고리 필터링
  const categoryFiltered = isPopularSelected
    ? (popularTemplates ?? []).map((tpl) => ({
        ...tpl,
        categoryId: tpl.categoryId,
        categoryName: getCategoryDisplayName({
          id: tpl.categoryId,
          name: tpl.categoryName,
          nameI18n: tpl.categoryNameI18n,
        }),
      }))
    : selectedCategoryId
      ? allTemplates.filter((tpl) => tpl.categoryId === selectedCategoryId)
      : allTemplates

  // 검색어로 추가 필터링 (원문과 현재 언어 번역 모두 매칭)
  const filteredTemplates = searchQuery.trim()
    ? categoryFiltered.filter((tpl) => {
        const query = searchQuery.trim().toLowerCase()
        return [
          tpl.name,
          localizeName(tpl),
          tpl.description ?? '',
          localizeDescription(tpl) ?? '',
        ].some((text) => text.toLowerCase().includes(query))
      })
    : categoryFiltered

  // 필터/검색/탭 전환 시 처음부터 다시 보여준다
  // (prop 변화 시 렌더 도중 상태를 리셋하는 React 권장 패턴 — effect 불필요)
  const filterKey = `${selectedCategoryId ?? "all"}::${searchQuery.trim()}`
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey)
  if (filterKey !== prevFilterKey) {
    setPrevFilterKey(filterKey)
    setVisibleCount(TEMPLATES_PAGE_SIZE)
  }

  // 클라이언트 windowing: 받아온 전체 목록을 24개씩 점진 렌더(서버 페이징 대신 즉시 검색/필터 유지)
  const visibleTemplates = filteredTemplates.slice(0, visibleCount)
  const hasMore = visibleCount < filteredTemplates.length

  // sentinel이 보이면 24개씩 더 렌더 (더 보여줄 게 없으면 sentinel 미렌더 → 관찰 중단)
  useEffect(() => {
    if (!hasMore) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) => prev + TEMPLATES_PAGE_SIZE)
        }
      },
      { threshold: 0.1 }
    )
    if (observerTarget.current) {
      observer.observe(observerTarget.current)
    }
    return () => observer.disconnect()
  }, [hasMore])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-500">
        <p>{t('templates.loadFailed')}</p>
        <p className="text-sm text-slate-400 mt-1">{t('common.tryAgainLater')}</p>
      </div>
    )
  }

  if (!categories || categories.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
          <Layout className="w-10 h-10 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          {t('templates.emptyTitle')}
        </h3>
        <p className="text-slate-500 text-sm">
          {t('templates.emptyDescription')}
        </p>
      </div>
    )
  }

  return (
    <div className="py-2 space-y-4">
      {/* 카테고리 필터 + 검색 */}
      <div className="flex items-center lg:items-start gap-2 pb-2">
        {/* 카테고리 버튼 - 모바일에서 가로 스크롤, 데스크톱에서 wrap */}
        <div className="flex gap-2 overflow-x-auto min-w-0 lg:flex-wrap lg:overflow-visible scrollbar-hide">
          <button
            onClick={() => onCategoryChange(null)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap shrink-0 ${
              selectedCategoryId === null
                ? "bg-primary text-primary-foreground"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {t('templates.allCategories')}
          </button>
          <button
            onClick={() => onCategoryChange('popular')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap shrink-0 flex items-center gap-1 ${
              isPopularSelected
                ? "bg-primary text-primary-foreground"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            <Flame className="w-3.5 h-3.5" />
            {t('templates.popular')}
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => onCategoryChange(category.id)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap shrink-0 ${
                selectedCategoryId === category.id
                  ? "bg-primary text-primary-foreground"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {getCategoryDisplayName(category)}
            </button>
          ))}
        </div>

        {/* 인라인 검색 - 스크롤 영역 밖에서 우측 고정 */}
        <div
          className={`relative shrink-0 transition-all duration-200 ease-in-out ${
            isSearchOpen ? "w-[180px]" : "w-9"
          }`}
        >
          {isSearchOpen ? (
            <>
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onBlur={() => {
                  if (!searchQuery.trim()) {
                    setIsSearchOpen(false)
                  }
                }}
                placeholder={t('templates.searchPlaceholder')}
                className="w-full h-9 pl-8 pr-7 text-sm border border-slate-200 rounded-full bg-white text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-200 focus:border-slate-200 transition-colors"
              />
              {searchQuery && (
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setSearchQuery("")
                    searchInputRef.current?.focus()
                  }}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                  aria-label={t('templates.clearSearch')}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </>
          ) : (
            <button
              onClick={() => {
                setIsSearchOpen(true)
                requestAnimationFrame(() => searchInputRef.current?.focus())
              }}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors"
              aria-label={t('templates.search')}
            >
              <Search className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* 인기 탭 로딩 상태 */}
      {isPopularSelected && isPopularLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : isPopularSelected && !isPopularLoading && filteredTemplates.length === 0 && !searchQuery.trim() ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Flame className="w-10 h-10 text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">{t('templates.popularEmptyTitle')}</p>
          <p className="text-xs text-slate-400 mt-1">{t('templates.popularEmptyDescription')}</p>
        </div>
      ) : /* 좌→우 행 맞춤 그리드 */
      filteredTemplates.length > 0 ? (
        <>
          <JustifiedRows
            items={visibleTemplates}
            getKey={(template) => template.id}
            getAspectRatio={(template) => template.aspectRatio}
            renderItem={(template) => (
              <TemplateCard
                template={template}
                categoryName={template.categoryName}
                onUse={() => handleUseTemplate(template)}
                onPreview={() => setPreviewTemplate(template)}
              />
            )}
          />
          {hasMore && (
            <div
              ref={observerTarget}
              className="flex items-center justify-center py-8"
            >
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          )}
        </>
      ) : searchQuery.trim() ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Search className="w-10 h-10 text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">
            <Trans
              i18nKey="templates.noSearchResults"
              values={{ query: searchQuery.trim() }}
              components={{ strong: <span className="font-medium text-slate-700" /> }}
            />
          </p>
        </div>
      ) : null}

      <TemplatePreviewModal
        template={previewTemplate}
        isOpen={previewTemplate !== null}
        onClose={() => setPreviewTemplate(null)}
        onUse={() => previewTemplate && handleUseTemplate(previewTemplate)}
      />
    </div>
  )
}
